import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPopularRouteGroup,
  createPopularRouteItem,
  listPopularRouteGroups,
  listPopularRouteItems,
  togglePopularRouteGroup,
  togglePopularRouteItem,
  updatePopularRouteGroup,
  updatePopularRouteItem,
  uploadAdminImage,
} from '../../../api/cabsAdmin';
import useCabLookups from './useCabLookups';

const emptyGroup = {
  fromLocationId: '',
  title: '',
  sortOrder: 0,
  isActive: true,
};

const emptyItem = {
  groupId: '',
  toLocationId: '',
  imageUrl: '',
  sortOrder: 0,
  isActive: true,
};

const AdminCabsPopularRoutes = () => {
  const queryClient = useQueryClient();
  const { locations } = useCabLookups();
  const [activeGroupId, setActiveGroupId] = useState('');
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [groupForm, setGroupForm] = useState(emptyGroup);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const locationOptions = useMemo(
    () => locations.map((loc) => ({ value: loc.id, label: `${loc.name} (${loc.city})` })),
    [locations]
  );

  const groupsQuery = useQuery({
    queryKey: ['cabs', 'popular-route-groups'],
    queryFn: () => listPopularRouteGroups({ limit: 100 }),
  });

  const groups = useMemo(
    () => groupsQuery.data?.data || groupsQuery.data || [],
    [groupsQuery.data]
  );

  useEffect(() => {
    if (!activeGroupId && groups.length) {
      setActiveGroupId(groups[0].id);
    }
  }, [activeGroupId, groups]);

  const itemsQuery = useQuery({
    queryKey: ['cabs', 'popular-route-items', activeGroupId],
    queryFn: () => listPopularRouteItems({ groupId: activeGroupId, limit: 200 }),
    enabled: Boolean(activeGroupId),
  });

  const items = itemsQuery.data?.data || itemsQuery.data || [];

  const groupMutation = useMutation({
    mutationFn: (payload) =>
      payload.id ? updatePopularRouteGroup(payload.id, payload.data) : createPopularRouteGroup(payload.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cabs', 'popular-route-groups'] });
      setGroupModalOpen(false);
      setGroupForm(emptyGroup);
      setEditingGroup(null);
    },
    onError: (err) => setError(err.message || 'Failed to save group'),
  });

  const itemMutation = useMutation({
    mutationFn: (payload) =>
      payload.id ? updatePopularRouteItem(payload.id, payload.data) : createPopularRouteItem(payload.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cabs', 'popular-route-items', activeGroupId] });
      setItemModalOpen(false);
      setItemForm((prev) => ({ ...emptyItem, groupId: prev.groupId || activeGroupId }));
      setEditingItem(null);
    },
    onError: (err) => setError(err.message || 'Failed to save item'),
  });

  const toggleGroup = async (group) => {
    setError('');
    try {
      await togglePopularRouteGroup(group.id, !group.isActive);
      await queryClient.invalidateQueries({ queryKey: ['cabs', 'popular-route-groups'] });
    } catch (err) {
      setError(err.message || 'Failed to update group status');
    }
  };

  const toggleItem = async (item) => {
    setError('');
    try {
      await togglePopularRouteItem(item.id, !item.isActive);
      await queryClient.invalidateQueries({ queryKey: ['cabs', 'popular-route-items', activeGroupId] });
    } catch (err) {
      setError(err.message || 'Failed to update item status');
    }
  };

  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm(emptyGroup);
    setGroupModalOpen(true);
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      fromLocationId: group.fromLocationId || '',
      title: group.title || '',
      sortOrder: group.sortOrder || 0,
      isActive: group.isActive ?? true,
    });
    setGroupModalOpen(true);
  };

  const openCreateItem = () => {
    setEditingItem(null);
    setItemForm({ ...emptyItem, groupId: activeGroupId });
    setItemModalOpen(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      groupId: item.groupId || activeGroupId,
      toLocationId: item.toLocationId || '',
      imageUrl: item.imageUrl || '',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive ?? true,
    });
    setItemModalOpen(true);
  };

  const handleGroupSave = async () => {
    setError('');
    if (!groupForm.title || !groupForm.fromLocationId) {
      setError('From city and title are required.');
      return;
    }
    groupMutation.mutate({
      id: editingGroup?.id,
      data: {
        ...groupForm,
        sortOrder: Number(groupForm.sortOrder || 0),
      },
    });
  };

  const handleItemSave = async () => {
    setError('');
    if (!itemForm.groupId || !itemForm.toLocationId) {
      setError('Group and destination city are required.');
      return;
    }
    itemMutation.mutate({
      id: editingItem?.id,
      data: {
        ...itemForm,
        sortOrder: Number(itemForm.sortOrder || 0),
        imageUrl: itemForm.imageUrl || null,
      },
    });
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const uploaded = await uploadAdminImage(file);
      const url = uploaded?.path || uploaded?.url || uploaded?.filePath || uploaded?.data?.path || '';
      setItemForm((prev) => ({ ...prev, imageUrl: url }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600">Cab Services</p>
          <h1 className="text-2xl font-semibold text-slate-900">Popular Routes</h1>
          <p className="text-sm text-slate-600">Manage landing page route blocks and their destinations.</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateGroup}>
          Add group
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-4">
        <div className="col-lg-6">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">Route Groups</h2>
            </div>
            <div className="table-responsive mt-3 border rounded-lg">
              <table className="table table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>From City</th>
                    <th>Title</th>
                    <th>Sort</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupsQuery.isLoading && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        Loading groups...
                      </td>
                    </tr>
                  )}
                  {!groupsQuery.isLoading && groups.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        No groups yet.
                      </td>
                    </tr>
                  )}
                  {groups.map((group) => (
                    <tr key={group.id} className={group.id === activeGroupId ? 'table-active' : ''}>
                      <td>{group.fromLocation?.name || '-'}</td>
                      <td>{group.title}</td>
                      <td>{group.sortOrder}</td>
                      <td>
                        <span className={`badge ${group.isActive ? 'bg-success-subtle text-success' : 'bg-secondary'}`}>
                          {group.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="btn-group">
                          <button className="btn btn-sm btn-outline-primary" onClick={() => setActiveGroupId(group.id)}>
                            View
                          </button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => openEditGroup(group)}>
                            Edit
                          </button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleGroup(group)}>
                            {group.isActive ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">Route Items</h2>
              <button className="btn btn-outline-primary btn-sm" onClick={openCreateItem} disabled={!activeGroupId}>
                Add item
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {activeGroupId ? 'Manage destinations inside the selected group.' : 'Select a group to manage destinations.'}
            </p>
            <div className="table-responsive mt-3 border rounded-lg">
              <table className="table table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>To City</th>
                    <th>Image</th>
                    <th>Sort</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {itemsQuery.isLoading && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        Loading items...
                      </td>
                    </tr>
                  )}
                  {!itemsQuery.isLoading && items.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted">
                        No destinations yet.
                      </td>
                    </tr>
                  )}
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.toLocation?.name || '-'}</td>
                      <td>
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.toLocation?.name || 'Route'} className="h-8 w-8 rounded-md object-cover" />
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td>{item.sortOrder}</td>
                      <td>
                        <span className={`badge ${item.isActive ? 'bg-success-subtle text-success' : 'bg-secondary'}`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="btn-group">
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => openEditItem(item)}>
                            Edit
                          </button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleItem(item)}>
                            {item.isActive ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal show={groupModalOpen} onHide={() => setGroupModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingGroup ? 'Edit Group' : 'Create Group'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">From City</label>
            <select
              className="form-select"
              value={groupForm.fromLocationId}
              onChange={(e) => setGroupForm((prev) => ({ ...prev, fromLocationId: e.target.value }))}
            >
              <option value="">Select city</option>
              {locationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Title</label>
            <input
              className="form-control"
              value={groupForm.title}
              onChange={(e) => setGroupForm((prev) => ({ ...prev, title: e.target.value }))}
            />
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Sort Order</label>
              <input
                type="number"
                className="form-control"
                value={groupForm.sortOrder}
                onChange={(e) => setGroupForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={groupForm.isActive}
                  onChange={(e) => setGroupForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  id="groupActive"
                />
                <label className="form-check-label" htmlFor="groupActive">
                  Active
                </label>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setGroupModalOpen(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleGroupSave} disabled={groupMutation.isPending}>
            {groupMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={itemModalOpen} onHide={() => setItemModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingItem ? 'Edit Destination' : 'Add Destination'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">To City</label>
            <select
              className="form-select"
              value={itemForm.toLocationId}
              onChange={(e) => setItemForm((prev) => ({ ...prev, toLocationId: e.target.value }))}
            >
              <option value="">Select city</option>
              {locationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Image URL</label>
            <input
              className="form-control"
              value={itemForm.imageUrl || ''}
              onChange={(e) => setItemForm((prev) => ({ ...prev, imageUrl: e.target.value }))}
            />
            <div className="mt-2">
              <input
                className="form-control"
                type="file"
                accept="image/*"
                onChange={handleUpload}
                disabled={uploading}
              />
              <p className="text-xs text-slate-500 mt-1">Optional: upload an icon/image.</p>
            </div>
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Sort Order</label>
              <input
                type="number"
                className="form-control"
                value={itemForm.sortOrder}
                onChange={(e) => setItemForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={itemForm.isActive}
                  onChange={(e) => setItemForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  id="itemActive"
                />
                <label className="form-check-label" htmlFor="itemActive">
                  Active
                </label>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setItemModalOpen(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleItemSave} disabled={itemMutation.isPending}>
            {itemMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminCabsPopularRoutes;
